/**
 * Free Gift Manager for Shopify Cart
 * Handles free gift offers based on cart value threshold
 * 
 * Features:
 * - Monitor cart value for ₹3999 threshold
 * - Show gift selection modal when threshold is reached
 * - Add selected gifts as free items
 * - Maintain gift status during cart updates
 * - Remove gifts when cart falls below threshold
 */

window.CartManager = {
  // Configuration
  config: {
    threshold: 399900, // ₹3999 in cents (Shopify uses cents)
    giftProductIds: [
      // UPDATE THESE WITH YOUR ACTUAL BASEBALL CAP PRODUCT IDs
      // Example: 7234567890123, 7234567890124, 7234567890125, 7234567890126
    ],
    giftVariantIds: [
      // UPDATE THESE WITH YOUR ACTUAL BASEBALL CAP VARIANT IDs
      // Example: 41234567890123, 41234567890124, 41234567890125, 41234567890126
    ],
    giftLineItemProperty: '_free_gift', // Property to identify free gifts
    freeGiftDiscountCode: 'FREE_GIFT_3999' // Discount code for free gifts (optional)
  },

  // State management
  state: {
    cartTotal: 0,
    hasEligibleGift: false,
    isProcessing: false,
    modalShown: false,
    currentCart: null
  },

  // Initialize the cart manager
  init() {
    console.log('🎁 Free Gift Manager initialized');
    this.bindEvents();
    this.monitorCart();
    
    // Initial cart check
    this.checkCartStatus();
  },

  // Bind event listeners
  bindEvents() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      console.log('Cart updated event received');
      this.handleCartUpdate(event.detail);
    });

    // Listen for item additions
    document.addEventListener('cart:item-added', (event) => {
      console.log('Item added to cart');
      this.handleCartUpdate();
    });

    // Listen for item removals
    document.addEventListener('cart:item-removed', (event) => {
      console.log('Item removed from cart');
      this.handleCartUpdate();
    });

    // Listen for quantity changes
    document.addEventListener('cart:quantity-changed', (event) => {
      console.log('Cart quantity changed');
      this.handleCartUpdate();
    });

    // Fallback: Monitor cart changes with MutationObserver
    this.observeCartChanges();
  },

  // Monitor cart changes using MutationObserver as fallback
  observeCartChanges() {
    const cartElements = [
      document.querySelector('[data-cart-count]'),
      document.querySelector('[data-cart-total]'),
      document.querySelector('.cart-drawer'),
      document.querySelector('#CartDrawer')
    ].filter(Boolean);

    if (cartElements.length > 0) {
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            shouldCheck = true;
          }
        });
        
        if (shouldCheck) {
          setTimeout(() => this.handleCartUpdate(), 100);
        }
      });

      cartElements.forEach(element => {
        observer.observe(element, {
          childList: true,
          subtree: true,
          characterData: true
        });
      });
    }
  },

  // Monitor cart with periodic checks
  monitorCart() {
    // Check cart status every 2 seconds as fallback
    setInterval(() => {
      this.checkCartStatus();
    }, 2000);
  },

  // Handle cart updates
  async handleCartUpdate(cartData = null) {
    if (this.state.isProcessing) return;

    try {
      // ✅ CRITICAL FIX: Only proceed if Free Gift System is ready and products are loaded
      if (!window.FreeGiftSystemReady || !window.FreeGiftProducts || window.FreeGiftProducts.length === 0) {
        console.log('⏳ Free Gift System not ready yet, skipping modal trigger');
        console.log('  - System ready:', !!window.FreeGiftSystemReady);
        console.log('  - Products available:', window.FreeGiftProducts?.length || 0);
        
        // ✅ HIDE ANY EXISTING MODAL that might be showing while products aren't ready
        if (window.FreeGiftModal && typeof window.FreeGiftModal.hide === 'function') {
          window.FreeGiftModal.hide();
          console.log('🚫 Hiding any premature modal display');
        }
        return;
      }

      // Get current cart
      const cart = cartData || await this.getCurrentCart();
      this.state.currentCart = cart;

      // Calculate cart total excluding gifts
      const totalWithoutGifts = this.calculateCartTotalWithoutGifts(cart);
      
      console.log(`Cart total (excluding gifts): ₹${totalWithoutGifts / 100}`);
      
      // Check if threshold is met
      const thresholdMet = totalWithoutGifts >= this.config.threshold;
      const hasGift = this.hasGiftInCart(cart);

      // ✅ FIX: Handle different scenarios with proper skip logic
      if (thresholdMet && !hasGift && !this.isCurrentlySkipped()) {
        // Show gift selection modal when they qualify and haven't skipped recently
        console.log('✅ User qualifies for gift - products ready, showing modal');
        this.showGiftModal();
      } else if (!thresholdMet && hasGift) {
        // Remove gift if cart falls below threshold and clear skip status
        console.log('⚠️ Cart below threshold, removing gift');
        this.clearSkipStatus(); // Allow modal to show again when they re-qualify
        await this.removeGiftFromCart();
      }

      this.state.cartTotal = totalWithoutGifts;
      this.state.hasEligibleGift = hasGift;

    } catch (error) {
      console.error('Error handling cart update:', error);
    }
  },

  // Check current cart status
  async checkCartStatus() {
    try {
      const cart = await this.getCurrentCart();
      await this.handleCartUpdate(cart);
    } catch (error) {
      console.error('Error checking cart status:', error);
    }
  },

  // Get current cart via Shopify Cart API
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [], total_price: 0 };
    }
  },

  // Calculate cart total excluding free gifts
  calculateCartTotalWithoutGifts(cart) {
    if (!cart || !cart.items) return 0;

    return cart.items.reduce((total, item) => {
      // Skip items that are marked as free gifts
      const isFreeGift = item.properties && item.properties[this.config.giftLineItemProperty];
      const isGiftProduct = this.config.giftVariantIds.includes(item.variant_id);
      
      if (isFreeGift || isGiftProduct) {
        return total; // Don't count gift items
      }
      
      return total + item.final_line_price;
    }, 0);
  },

  // Check if cart has a gift item
  hasGiftInCart(cart) {
    if (!cart || !cart.items) return false;

    return cart.items.some(item => {
      const isFreeGift = item.properties && item.properties[this.config.giftLineItemProperty];
      const isGiftProduct = this.config.giftVariantIds.includes(item.variant_id);
      return isFreeGift || isGiftProduct;
    });
  },

  // Show gift selection modal
  showGiftModal() {
    // ✅ FIX: Remove modalShown restriction - allow modal to show every time
    console.log('🎁 Showing free gift modal');
    this.state.modalShown = true;
    
    if (window.FreeGiftModal) {
      window.FreeGiftModal.show();
    }
  },

  // Add selected gift to cart
  async addFreeGift(variantId) {
    if (this.state.isProcessing) return;
    
    this.state.isProcessing = true;
    
    try {
      console.log(`🎁 Adding free gift variant: ${variantId}`);

      // Prepare cart add data (exactly like normal add-to-cart)
      const formData = {
        'items': [{
          'id': variantId,
          'quantity': 1,
          'properties': {
            [this.config.giftLineItemProperty]: 'true',
            '_gift_threshold': this.config.threshold.toString(),
            '_gift_message': 'Free gift with purchase over ₹3999'
          }
        }]
      };

      // ✅ USE THEME'S CART API SYSTEM (like ProductForm does)
      const cartConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      };

      // Add to cart using the theme's route system
      const cartAddUrl = window.routes ? window.routes.cart_add_url : '/cart/add.js';
      const response = await fetch(cartAddUrl, cartConfig);

      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Free gift added successfully:', result);

      // ✅ USE THEME'S CART DRAWER SYSTEM (exactly like ProductForm)
      const cartDrawer = document.querySelector('cart-drawer');
      
      if (cartDrawer) {
        console.log('🔄 Using theme cart drawer system...');
        
        // Method 1: Use the cart drawer's update method (like theme does)
        if (typeof cartDrawer.update === 'function') {
          console.log('✅ Calling cartDrawer.update()');
          cartDrawer.update();
        }
        
        // Method 2: Trigger the cart drawer to refresh its content
        else if (cartDrawer.cartAPI && typeof cartDrawer.cartAPI.getDrawer === 'function') {
          console.log('✅ Using cartDrawer.cartAPI.getDrawer()');
          cartDrawer.cartAPI.getDrawer()
            .then(responseText => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(responseText, 'text/html');
              const newContent = doc.querySelector('[data-cart-drawer-results]');
              const currentContent = cartDrawer.querySelector('[data-cart-drawer-results]');
              
              if (newContent && currentContent) {
                currentContent.innerHTML = newContent.innerHTML;
                console.log('✅ Cart drawer content updated');
              }
            });
        }
        
        // Method 3: Manual refresh using theme's section reload
        else {
          console.log('✅ Using manual section reload');
          const sectionId = cartDrawer.dataset.sectionId || 'cart-drawer';
          fetch(`${window.location.pathname}?section_id=${sectionId}`)
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newResults = doc.querySelector('[data-cart-drawer-results]');
              const currentResults = cartDrawer.querySelector('[data-cart-drawer-results]');
              
              if (newResults && currentResults) {
                currentResults.innerHTML = newResults.innerHTML;
                console.log('✅ Cart drawer manually refreshed');
              }
            });
        }
        
        // Open cart drawer (like ProductForm does)
        setTimeout(() => {
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
            console.log('✅ Cart drawer opened');
          } else {
            cartDrawer.setAttribute('open', '');
            cartDrawer.classList.add('is-open');
            console.log('✅ Cart drawer opened manually');
          }
        }, 100); // Small delay to ensure content loads first
      }
      
      // Hide the free gift modal
      if (window.FreeGiftModal && window.FreeGiftModal.hide) {
        window.FreeGiftModal.hide();
      }
      
      // ✅ Trigger cart count updates and other global cart listeners
      const cartResponse = await fetch('/cart.js');
      const updatedCart = await cartResponse.json();
      
      // Dispatch standard cart events that theme components listen for
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: updatedCart }
      }));
      
      // Update cart count displays
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = updatedCart.item_count;
      });
      
      console.log('🎉 Free gift added using theme cart system! Cart drawer should refresh and open.');
      
      return result;

    } catch (error) {
      console.error('❌ Error adding free gift:', error);
      throw error;
    } finally {
      this.state.isProcessing = false;
    }
  },

  // Remove gift from cart
  async removeGiftFromCart() {
    if (this.state.isProcessing) return;
    
    this.state.isProcessing = true;

    try {
      const cart = await this.getCurrentCart();
      
      // Find gift items
      const giftItems = cart.items.filter(item => {
        const isFreeGift = item.properties && item.properties[this.config.giftLineItemProperty];
        const isGiftProduct = this.config.giftVariantIds.includes(item.variant_id);
        return isFreeGift || isGiftProduct;
      });

      if (giftItems.length === 0) return;

      console.log('Removing free gifts from cart');

      // Remove each gift item
      for (const item of giftItems) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'id': item.key,
            'quantity': 0
          })
        });
      }

      // Update cart display using theme's cart drawer system
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.update === 'function') {
        cartDrawer.update();
      }
      
      // Reset modal state
      this.state.modalShown = false;
      sessionStorage.removeItem('freeGiftReceived');
      sessionStorage.removeItem('freeGiftSkipped');

    } catch (error) {
      console.error('Error removing gift from cart:', error);
    } finally {
      this.state.isProcessing = false;
    }
  },

  // ✅ Removed refreshCartDisplay - now using theme's cart drawer system directly

  // ✅ SMART SKIP TRACKING - prevents infinite loops but allows re-triggering
  isCurrentlySkipped() {
    const skipData = sessionStorage.getItem('freeGiftSkipped');
    if (!skipData) return false;
    
    try {
      const { timestamp, cartTotal } = JSON.parse(skipData);
      const now = Date.now();
      const SKIP_DURATION = 5 * 60 * 1000; // 5 minutes
      
      // Skip is valid if:
      // 1. Less than 5 minutes have passed, AND
      // 2. Cart total hasn't changed significantly (they haven't added new items)
      const timeValid = (now - timestamp) < SKIP_DURATION;
      const cartSimilar = Math.abs(this.state.cartTotal - cartTotal) < 100; // ₹1 tolerance
      
      return timeValid && cartSimilar;
    } catch {
      return false;
    }
  },
  
  markAsSkipped() {
    const skipData = {
      timestamp: Date.now(),
      cartTotal: this.state.cartTotal
    };
    sessionStorage.setItem('freeGiftSkipped', JSON.stringify(skipData));
    console.log('🚫 User skipped gift - will not show again for 5 minutes unless cart changes');
  },
  
  clearSkipStatus() {
    sessionStorage.removeItem('freeGiftSkipped');
    console.log('🔄 Skip status cleared - modal can show again');
  },

  // Format money for display
  formatMoney(cents) {
    return `₹${(cents / 100).toLocaleString('en-IN')}`;
  },

  // Get gift products data
  async getGiftProducts() {
    try {
      // This should be replaced with actual product API calls
      const productPromises = this.config.giftProductIds.map(async (productId) => {
        const response = await fetch(`/products/${productId}.js`);
        if (response.ok) {
          return await response.json();
        }
        return null;
      });

      const products = await Promise.all(productPromises);
      return products.filter(product => product !== null);

    } catch (error) {
      console.error('Error fetching gift products:', error);
      return [];
    }
  }
};

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Small delay to ensure other cart scripts are loaded
  setTimeout(() => {
    window.CartManager.init();
  }, 500);
});

// Initialize immediately if DOM already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => window.CartManager.init(), 500);
  });
} else {
  setTimeout(() => window.CartManager.init(), 500);
}
